import React, { useContext, useEffect, useRef, useState } from 'react';
import { PropTypes } from 'prop-types';
import { Divider, Progress } from '@mantine/core';
import NumberFormat from 'react-number-format';
import { Context as RegistrationContext } from '../../../providers/RegistrationProvider';
import { triggerNotification } from '../../../helpers/notificationHelper';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppTitle from '../../common/AppTitle';
import AppStack from '../../common/AppStack';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import CustomerTransactionList from './CustomerTransactionList';
import { getResponsiveStyle as rs } from '../../../helpers/styles';
import { useMediaQueryIndex } from '../../../helpers/hooks';
import {
  PAYMENT_TYPE_ENUM,
  REG_FORM_SUBMISSION_INVOICE_STATUS_ENUM
} from '../../../config/constants';
import { getInvoiceStatusInfo } from '../../../helpers/customerPaymentsHelper';

const CustomerInvoicePriceItem = ({
  color,
  isDividerHidden,
  label,
  value,
  weight
}) => (
  <>
    <AppFlexbox
      style={{
        flexWrap: 'nowrap',
        gap: 10,
        marginTop: 15,
        alignItems: 'center'
      }}
    >
      <AppText style={{ fontSize: 16 }} weight={weight ?? 'normal'}>
        {label}
      </AppText>
      <Divider style={{ flex: 1 }} variant="dotted" />
      <AppText style={{ fontSize: 16, color }} weight={weight}>
        <NumberFormat
          decimalScale={2}
          displayType="text"
          fixedDecimalScale
          prefix="$"
          thousandSeparator
          value={value}
        />
      </AppText>
    </AppFlexbox>
    {!isDividerHidden && <Divider />}
  </>
);

CustomerInvoicePriceItem.propTypes = {
  color: PropTypes.string,
  isDividerHidden: PropTypes.bool,
  label: PropTypes.string,
  value: PropTypes.number,
  weight: PropTypes.number
};

const CustomerInvoicePaymentsModal = ({ invoice, isOpen, onClose }) => {
  const mqIndex = useMediaQueryIndex();
  const requestedPk = useRef(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const { state, fetchRegFormSubmissionBalance } = useContext(
    RegistrationContext
  );
  const balance =
    !loadFailed &&
    state.regFormSubmissionBalance.value?.pkRegFormSubmission?.toString() ===
      invoice?.pkRegFormSubmission?.toString()
      ? state.regFormSubmissionBalance.value
      : null;
  const isLoading =
    !loadFailed && (state.regFormSubmissionBalance.loading || !balance);

  const regFormSubmissionInvoice =
    balance?.regFormSubmissionPayments?.find(
      (p) =>
        p.regFormSubmissionInvoice?.pkRegFormSubmissionInvoice ===
        invoice?.pkRegFormSubmissionInvoice
    )?.regFormSubmissionInvoice ?? null;

  const allPayments = regFormSubmissionInvoice?.regFormSubmissionPayments ?? [];
  const invoicePayment =
    allPayments.find(
      (p) =>
        p.fkRegFormSubmissionPaymentType?.toString() ===
        PAYMENT_TYPE_ENUM.INVOICE
    ) ?? null;
  const viewablePayments = allPayments.filter(
    (p) =>
      p.fkRegFormSubmissionPaymentType?.toString() !== PAYMENT_TYPE_ENUM.INVOICE
  );

  // Every figure in this modal is in DOLLARS, not cents
  const amountPaid = viewablePayments
    .filter(
      (p) =>
        p.fkRegFormSubmissionPaymentType?.toString() !==
        PAYMENT_TYPE_ENUM.REFUND
    )
    .reduce((r, c) => r + c.amount, 0);
  const amountRefunded =
    viewablePayments
      .filter(
        (p) =>
          p.fkRegFormSubmissionPaymentType?.toString() ===
          PAYMENT_TYPE_ENUM.REFUND
      )
      .reduce((r, c) => r + c.amount, 0) * -1;
  const isPartialRefund = amountRefunded > 0 && amountPaid - amountRefunded > 0;

  const paidStatus = getInvoiceStatusInfo(
    REG_FORM_SUBMISSION_INVOICE_STATUS_ENUM.PAID
  );
  const voidStatus = getInvoiceStatusInfo(
    REG_FORM_SUBMISSION_INVOICE_STATUS_ENUM.VOID
  );
  const uncollectibleStatus = getInvoiceStatusInfo(
    REG_FORM_SUBMISSION_INVOICE_STATUS_ENUM.UNCOLLECTIBLE
  );

  const invoiceTotal = invoicePayment?.amount ?? 0;
  const transactionTotal = invoicePayment ? invoiceTotal - amountRefunded : 0;
  const totalPayments = amountPaid - amountRefunded;

  useEffect(() => {
    if (isOpen && invoice?.pkRegFormSubmission) {
      const { pkRegFormSubmission } = invoice;
      requestedPk.current = pkRegFormSubmission;
      setLoadFailed(false);
      fetchRegFormSubmissionBalance(pkRegFormSubmission, null, (error) => {
        if (requestedPk.current === pkRegFormSubmission) {
          triggerNotification(error);
          setLoadFailed(true);
        }
      });
    }
  }, [isOpen, invoice?.pkRegFormSubmission]);

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      size={1100}
      title={<AppTitle order={3}>Invoice Payments</AppTitle>}
    >
      <AppStack style={{ gap: 20 }}>
        <Divider />

        {isLoading ? (
          <AppFlexbox style={{ padding: 40, justifyContent: 'center' }}>
            <AppText style={{ fontSize: 16 }}>Loading payments...</AppText>
          </AppFlexbox>
        ) : !invoicePayment ? (
          <AppFlexbox style={{ padding: 40, justifyContent: 'center' }}>
            <AppText style={{ fontSize: 16 }} weight={500}>
              Payment details are not available.
            </AppText>
          </AppFlexbox>
        ) : (
          <>
            <AppFlexbox
              style={{
                gap: rs([20, 20, 40], mqIndex),
                alignItems: 'start',
                flexDirection: rs(['column', 'column', 'row'], mqIndex)
              }}
            >
              <AppStack style={{ flex: 1, gap: 5, alignSelf: 'stretch' }}>
                <AppText size="lg" style={{ color: 'dodgerblue' }} weight={700}>
                  Payment Status
                </AppText>
                <Divider size={2} />

                {amountPaid - amountRefunded >= invoiceTotal ? (
                  <AppStack style={{ gap: 0 }}>
                    <AppText
                      style={{ fontSize: 16, color: paidStatus.color }}
                      weight={500}
                    >
                      Paid in full
                    </AppText>
                    <Progress color={paidStatus.color} value={100} />
                  </AppStack>
                ) : regFormSubmissionInvoice.status ===
                  REG_FORM_SUBMISSION_INVOICE_STATUS_ENUM.VOID ? (
                  <AppStack style={{ gap: 0 }}>
                    <AppText
                      style={{ fontSize: 16, color: voidStatus.color }}
                      weight={500}
                    >
                      Void
                    </AppText>
                    <Progress value={0} />
                  </AppStack>
                ) : regFormSubmissionInvoice.status ===
                  REG_FORM_SUBMISSION_INVOICE_STATUS_ENUM.UNCOLLECTIBLE ? (
                  <AppStack style={{ gap: 0 }}>
                    <AppText
                      style={{ fontSize: 16, color: uncollectibleStatus.color }}
                      weight={500}
                    >
                      Uncollectible
                    </AppText>
                    <Progress value={0} />
                  </AppStack>
                ) : isPartialRefund ? (
                  <AppStack style={{ gap: 0 }}>
                    <AppText style={{ fontSize: 16 }} weight={500}>
                      Partial Refund
                    </AppText>
                    <Progress value={0} />
                  </AppStack>
                ) : amountRefunded > 0 ? (
                  <AppStack style={{ gap: 0 }}>
                    <AppText style={{ fontSize: 16 }} weight={500}>
                      Refunded
                    </AppText>
                    <Progress value={0} />
                  </AppStack>
                ) : (
                  <AppStack style={{ gap: 0 }}>
                    <AppText style={{ fontSize: 16 }} weight={500}>
                      Waiting for payment
                    </AppText>
                    <Progress
                      value={
                        transactionTotal > 0
                          ? (totalPayments / transactionTotal) * 100
                          : 0
                      }
                    />
                  </AppStack>
                )}
              </AppStack>

              <AppStack style={{ flex: 1, gap: 5, alignSelf: 'stretch' }}>
                <AppText
                  style={{ color: 'dodgerblue', fontSize: 18 }}
                  weight={700}
                >
                  Overview
                </AppText>
                <Divider size={2} />

                <CustomerInvoicePriceItem
                  label="Invoice Total"
                  value={invoiceTotal}
                />

                {amountRefunded > 0 && (
                  <CustomerInvoicePriceItem
                    label="Invoice Refunds"
                    value={amountRefunded}
                  />
                )}

                <CustomerInvoicePriceItem
                  label="Transaction Total"
                  value={transactionTotal}
                  weight={700}
                />

                <CustomerInvoicePriceItem
                  color="#37b24d"
                  label="Total Payments"
                  value={totalPayments}
                />

                <CustomerInvoicePriceItem
                  isDividerHidden
                  label="Balance"
                  value={transactionTotal - totalPayments}
                  weight={700}
                />
              </AppStack>
            </AppFlexbox>

            <CustomerTransactionList
              key={invoice?.pkRegFormSubmissionInvoice}
              payments={viewablePayments}
            />
          </>
        )}
      </AppStack>
    </ResponsiveModal>
  );
};

CustomerInvoicePaymentsModal.propTypes = {
  invoice: PropTypes.object,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func
};

export default CustomerInvoicePaymentsModal;
